import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, MessageCircle, Phone, X } from "lucide-react";
import { WaveformMark } from "./Logo";
import { scrollToId } from "../../lib/scroll";
import { PHONE_TEL, WHATSAPP_URL } from "../../constants/site";

// Same header on every page. Anchor items point at home-page sections;
// "How we work" is its own page. Desktop items open sub-section dropdowns
// on hover/focus while staying clickable themselves.
const NAV = [
  ["About us", "/#about-us"],
  ["Services", "/#services"],
  ["How we work", "/how-we-work"],
  ["FAQ", "/#faq"],
];

const SUBNAV = {
  "/#about-us": [
    ["Experience", "/#experience"],
    ["Credentials", "/#credentials"],
    ["Why Law Abiding Technology", "/#why-lat"],
    ["Where we work", "/#coverage"],
  ],
  "/#services": [
    ["What we provide", "/#service-options"],
    ["Programmes and the annual contract", "/#programme"],
  ],
  "/how-we-work": [
    ["The inspection", "/how-we-work#how-it-runs"],
    ["Where we look", "/how-we-work#process"],
    ["Methods and equipment", "/how-we-work#method"],
    ["The written report", "/how-we-work#reporting"],
    ["Confidentiality", "/how-we-work#confidentiality"],
  ],
};

const hashOf = (href) => (href.includes("#") ? href.split("#")[1] : null);

export default function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === "/";
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState(null);

  // Highlight follows the umbrella block in view (home only).
  useEffect(() => {
    if (!isHome) return undefined;
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-30% 0px -60% 0px" }
    );
    NAV.forEach(([, href]) => {
      const id = hashOf(href);
      if (!id) return;
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    // Clear the highlight while the hero area (above the first band) is in view.
    const onScroll = () => {
      const first = document.getElementById("about-us");
      if (first && window.scrollY < first.offsetTop - window.innerHeight * 0.4) {
        setActive(null);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      obs.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [isHome]);

  const go = (e, href) => {
    setMenuOpen(false);
    const [path, hash] = href.split("#");
    const samePage = (path === "/" && isHome) || path === pathname;
    if (hash && samePage && document.getElementById(hash)) {
      e.preventDefault();
      scrollToId(hash);
      return;
    }
    if (!hash && samePage) {
      e.preventDefault();
      scrollToId("top");
      return;
    }
    e.preventDefault();
    navigate(href);
  };

  const isActive = (href) => {
    if (!hashOf(href)) return pathname === href;
    return isHome && active === hashOf(href);
  };

  const linkClass = (href) =>
    `whitespace-nowrap text-sm transition-colors ${
      isActive(href) ? "font-semibold text-ink" : "text-sub hover:text-ink"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 border-b border-hairline ${
        menuOpen ? "bg-base-color" : "bg-base-color/85 backdrop-blur-xl"
      }`}
    >
      <div className="flex h-16 items-center justify-between gap-4 site-x">
        <a
          href="/#top"
          onClick={(e) => go(e, "/#top")}
          className="flex min-w-0 items-center gap-3"
          data-testid="header-brand-link"
          aria-label="TSCM Services — back to top"
        >
          <WaveformMark className="h-5 w-auto shrink-0 text-violet" />
          <span className="min-w-0 text-left leading-none">
            <span className="block font-head text-[1.1rem] font-extrabold tracking-tight sm:text-[1.35rem]">
              TSCM Services
            </span>
            <span className="mt-1 hidden whitespace-nowrap text-[0.5625rem] uppercase leading-relaxed tracking-[0.12em] text-sub sm:block">
              Law Abiding Technology · since 2005
            </span>
          </span>
        </a>

        <nav className="ml-auto hidden items-center gap-6 lg:flex" aria-label="Primary">
          {NAV.map(([label, href]) => {
            const subs = SUBNAV[href];
            const id = hashOf(href) || href.replace("/", "");
            return (
              <div key={href} className="group relative">
                <a
                  href={href}
                  onClick={(e) => go(e, href)}
                  data-testid={`nav-${id}-link`}
                  aria-current={isActive(href) ? "true" : undefined}
                  className={`${linkClass(href)} inline-flex items-center gap-1`}
                >
                  {label}
                  {subs && (
                    <ChevronDown
                      size={12}
                      className="opacity-60 transition-transform duration-200 group-hover:rotate-180"
                    />
                  )}
                </a>
                {subs && (
                  <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3 opacity-0 transition-opacity duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                    <ul
                      className="min-w-60 rounded-lg border border-hairline bg-surface py-2 shadow-xl shadow-black/50"
                      data-testid={`nav-${id}-dropdown`}
                    >
                      {subs.map(([sublabel, subhref]) => (
                        <li key={subhref}>
                          <a
                            href={subhref}
                            onClick={(e) => go(e, subhref)}
                            data-testid={`nav-sub-${hashOf(subhref)}-link`}
                            className="block whitespace-nowrap px-4 py-2 text-sm text-sub transition-colors hover:bg-raised hover:text-ink"
                          >
                            {sublabel}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="hidden items-center gap-2 lg:flex lg:pl-6">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-button"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-violet px-4 py-1.5 text-sm font-semibold text-[#1a1021] transition-colors hover:bg-violeth"
          >
            <MessageCircle size={14} />
            WhatsApp
          </a>
          <a
            href={PHONE_TEL}
            data-testid="header-call-button"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-ink/25 px-4 py-1.5 text-sm font-semibold transition-colors hover:border-violet hover:text-violet"
          >
            <Phone size={14} />
            Call
          </a>
        </div>

        <div className="ml-auto flex items-center gap-2 lg:hidden">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-mobile-whatsapp-button"
            aria-label="WhatsApp us"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-violet text-[#1a1021] transition-colors hover:bg-violeth"
          >
            <MessageCircle size={15} />
          </a>
          <a
            href={PHONE_TEL}
            data-testid="header-mobile-call-button"
            aria-label="Call us"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-hairline text-ink transition-colors hover:border-violet hover:text-violet"
          >
            <Phone size={15} />
          </a>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="header-menu-button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-hairline text-ink"
          >
            {menuOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          className="fixed inset-0 top-16 z-40 bg-base-color lg:hidden"
          data-testid="header-mobile-menu"
        >
          <nav className="flex h-full flex-col px-5 pb-10 pt-4" aria-label="Mobile">
            {NAV.map(([label, href]) => {
              const id = hashOf(href) || href.replace("/", "");
              return (
                <a
                  key={href}
                  href={href}
                  onClick={(e) => go(e, href)}
                  data-testid={`nav-mobile-${id}-link`}
                  className="border-b border-hairline py-4 text-lg font-semibold"
                >
                  {label}
                </a>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
